import React from "react";
import { WordCloud } from "@/components/WordCloud";
import { CHART_COLORS } from "@/lib/slides";
import { CheckCircle2, ThumbsUp, Users, Trophy } from "lucide-react";

function EmptyState({ text }) {
  return <div className="text-center text-slate-400 py-14 text-lg">{text}</div>;
}

export function ResultView({ slide, results, onToggleAnswered, revealed = false }) {
  if (!slide) return null;
  const type = slide.type;

  if (type === "multiple_choice") {
    const opts = results?.options || slide.options.map((label) => ({ label, count: 0 }));
    const total = opts.reduce((s, o) => s + o.count, 0);
    return (
      <div className="w-full space-y-4" data-testid="live-result-chart">
        {opts.map((o, i) => {
          const pct = total ? Math.round((o.count / total) * 100) : 0;
          return (
            <div key={i} className="w-full">
              <div className="flex justify-between items-baseline mb-1.5">
                <span className="font-semibold text-slate-800 text-lg truncate pr-4">{o.label}</span>
                <span className="font-mono-code text-slate-500 text-base tabular-nums">{pct}% · {o.count}</span>
              </div>
              <div className="h-9 w-full rounded-xl bg-slate-100 overflow-hidden">
                <div
                  className="h-full bar-grow rounded-xl"
                  style={{ width: `${Math.max(pct, o.count ? 3 : 0)}%`, background: CHART_COLORS[i % CHART_COLORS.length] }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === "word_cloud") {
    return <WordCloud words={results?.words} />;
  }

  if (type === "open_ended") {
    const list = results?.responses || [];
    if (!list.length) return <EmptyState text="Waiting for responses…" />;
    return (
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 w-full" data-testid="open-ended-results">
        {list.map((r, i) => (
          <div key={i} className="mb-4 break-inside-avoid rounded-xl border border-slate-200 bg-white p-4 shadow-sm animate-fade-up"
               style={{ animationDelay: `${i * 0.02}s` }}>
            <p className="text-slate-800 text-base leading-relaxed">{r.text}</p>
          </div>
        ))}
      </div>
    );
  }

  if (type === "scales") {
    const avg = results?.average || 0;
    const min = slide.scale_min, max = slide.scale_max;
    const pct = max > min ? ((avg - min) / (max - min)) * 100 : 0;
    const dist = results?.distribution || {};
    const maxCount = Math.max(1, ...Object.values(dist));
    return (
      <div className="w-full space-y-8" data-testid="scales-results">
        <div>
          <div className="flex justify-between text-sm font-semibold text-slate-500 mb-2">
            <span>{slide.scale_min_label || min}</span>
            <span>{slide.scale_max_label || max}</span>
          </div>
          <div className="relative h-4 rounded-full bg-slate-100">
            <div className="absolute -top-2 -translate-x-1/2 bar-grow" style={{ left: `${pct}%` }}>
              <div className="w-8 h-8 rounded-full bg-indigo-600 shadow-lg shadow-indigo-300 border-4 border-white" />
            </div>
          </div>
        </div>
        <div className="text-center">
          <div className="text-6xl font-display font-extrabold text-indigo-600 tabular-nums">{avg}</div>
          <div className="text-slate-400 text-sm mt-1">average · {results?.count || 0} votes</div>
        </div>
        <div className="flex items-end justify-center gap-3 h-24">
          {Array.from({ length: max - min + 1 }, (_, k) => min + k).map((n) => {
            const c = dist[String(n)] || 0;
            return (
              <div key={n} className="flex flex-col items-center gap-1">
                <div className="w-8 bar-grow rounded-t-md bg-indigo-400"
                     style={{ height: `${(c / maxCount) * 80 + 4}px` }} />
                <span className="text-xs font-mono-code text-slate-500">{n}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "ranking") {
    const ranked = results?.ranking || slide.options.map((label, i) => ({ label, score: 0, index: i }));
    const maxScore = Math.max(1, ...ranked.map((r) => r.score));
    return (
      <div className="w-full space-y-3" data-testid="ranking-results">
        {ranked.map((r, i) => (
          <div key={r.index} className="flex items-center gap-4">
            <div className="w-9 h-9 shrink-0 rounded-full bg-amber-100 text-amber-700 font-display font-bold flex items-center justify-center">
              {i + 1}
            </div>
            <div className="flex-1">
              <div className="flex justify-between mb-1">
                <span className="font-semibold text-slate-800">{r.label}</span>
                <span className="font-mono-code text-slate-500 text-sm">{r.score} pts</span>
              </div>
              <div className="h-6 rounded-lg bg-slate-100 overflow-hidden">
                <div className="h-full bar-grow rounded-lg bg-amber-500"
                     style={{ width: `${(r.score / maxScore) * 100}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "quiz") {
    const opts = results?.options || slide.options.map((label, i) => ({ label, count: 0, correct: i === slide.correct_option }));
    const total = opts.reduce((s, o) => s + o.count, 0);
    return (
      <div className="w-full space-y-4" data-testid="quiz-results">
        {opts.map((o, i) => {
          const pct = total ? Math.round((o.count / total) * 100) : 0;
          return (
            <div key={i} className="w-full">
              <div className="flex justify-between items-baseline mb-1.5">
                <span className={`font-semibold text-lg truncate pr-4 flex items-center gap-2 ${o.correct ? "text-emerald-600" : "text-slate-800"}`}>
                  {o.correct && <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />}{o.label}
                </span>
                <span className="font-mono-code text-slate-500 text-base tabular-nums">{pct}% · {o.count}</span>
              </div>
              <div className="h-9 w-full rounded-xl bg-slate-100 overflow-hidden">
                <div className={`h-full bar-grow rounded-xl ${o.correct && revealed ? "animate-reveal" : ""}`}
                     style={{ width: `${Math.max(pct, o.count ? 3 : 0)}%`, background: o.correct ? "#10B981" : "#CBD5E1" }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (type === "qa") {
    const questions = results?.questions || [];
    if (!questions.length) return <EmptyState text="No questions yet. Ask away!" />;
    return (
      <div className="w-full space-y-3" data-testid="qa-results">
        {questions.map((q) => (
          <div key={q.id}
               className={`rounded-xl border p-4 flex items-start gap-4 transition-all ${q.answered ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`}>
            <div className="flex flex-col items-center shrink-0">
              <ThumbsUp className="w-5 h-5 text-indigo-500" />
              <span className="font-mono-code font-bold text-slate-700 text-sm">{q.upvotes}</span>
            </div>
            <div className="flex-1">
              <p className={`text-slate-800 text-base ${q.answered ? "line-through text-slate-400" : ""}`}>{q.text}</p>
              <div className="text-xs text-slate-400 mt-1">— {q.author}</div>
            </div>
            {onToggleAnswered && (
              <button
                data-testid={`mark-answered-${q.id}`}
                onClick={() => onToggleAnswered(q.id)}
                className="shrink-0 text-slate-400 hover:text-emerald-600 transition-colors"
                title="Toggle answered"
              >
                <CheckCircle2 className={`w-6 h-6 ${q.answered ? "text-emerald-500" : ""}`} />
              </button>
            )}
          </div>
        ))}
      </div>
    );
  }

  return null;
}

export function ParticipantBadge({ count }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200"
          data-testid="participant-count-badge">
      <Users className="w-4 h-4" /> {count}
    </span>
  );
}

export function Leaderboard({ entries, title = "Leaderboard" }) {
  const [showAll, setShowAll] = React.useState(false);
  const full = entries || [];
  const list = showAll ? full : full.slice(0, 10);
  const medals = ["bg-amber-400 text-amber-900", "bg-slate-300 text-slate-700", "bg-orange-400 text-orange-900"];
  return (
    <div className="w-full" data-testid="quiz-leaderboard">
      <h3 className="font-display font-bold text-slate-900 text-lg mb-3 flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" /> {title}
      </h3>
      {full.length === 0 ? (
        <p className="text-slate-400 text-sm">No scores yet.</p>
      ) : (
        <>
          <div className="space-y-2">
            {list.map((e) => (
              <div key={e.rank} className="flex items-center gap-3 p-2.5 rounded-xl bg-white border border-slate-200 animate-fade-up"
                   style={{ animationDelay: `${Math.min(e.rank, 10) * 0.03}s` }}>
                <span className={`w-7 h-7 shrink-0 rounded-full flex items-center justify-center font-bold text-sm ${medals[e.rank - 1] || "bg-slate-100 text-slate-500"}`}>
                  {e.rank}
                </span>
                <span className="flex-1 font-semibold text-slate-800 truncate">{e.nickname}</span>
                <span className="font-mono-code font-bold text-indigo-600 tabular-nums">{e.score}</span>
              </div>
            ))}
          </div>
          {full.length > 10 && (
            <button data-testid="leaderboard-view-all" onClick={() => setShowAll((v) => !v)}
                    className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 hover:underline">
              {showAll ? "Show top 10" : `View all ${full.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}
